// ─────────────────────────────────────────────────────────────────────────────
// corporate-grid.tsx — Forum (c01) marketing card layout
//
// Design language: Stripe Sessions / Figma Config / ReactConf — IDE-like,
// tabular, monospace-anchored. The card itself is a tiny "preview pane" that
// teases the editorial polish of the full guest page (variant: agenda).
//
// Notes:
//   • All type uses tabular numerals via fontFeatureSettings: '"tnum" 1'.
//   • Hairline 1px keylines (no thicker) — engineering aesthetic.
//   • A faint dot-grid backdrop hints at the parallax scan in the hero.
//   • Photo strip falls back gracefully to a single hero photo.
//   • A bottom-right "TIER ★ ★ ★" stamp signals premium B2B tier.
// ─────────────────────────────────────────────────────────────────────────────
import { Photo, type LayoutCtx } from "./_shared";

export function CorporateGridLayout({ names, date, venue, accent, ink, font, photo, photos }: LayoutCtx) {
  const tnum = { fontFeatureSettings: '"tnum" 1, "lnum" 1' as const };
  const year = date.split(".")[2] ?? "2026";

  // Subtle dot-grid: 8px lattice, very low contrast — disappears at thumbnail
  // size but reads as "schematic / blueprint" up close.
  const dotGrid = `radial-gradient(circle, ${ink}11 1px, transparent 1px) 0 0 / 8px 8px`;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        padding: "8%",
        display: "grid",
        gridTemplateRows: "auto auto 1fr auto auto",
        gap: "4%",
        background: dotGrid,
        backgroundColor: "transparent",
      }}
    >
      {/* Top ticker — id, date, volume, tier */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${ink}`,
          paddingBottom: "3%",
          fontFamily: font,
          fontSize: 8,
          fontWeight: 500,
          letterSpacing: "0.18em",
          ...tnum,
        }}
      >
        <span>FORUM/{year}</span>
        <span style={{ opacity: 0.55 }}>VOL.01</span>
        <span>{date}</span>
      </div>

      {/* // 01 — section label */}
      <div
        style={{
          fontFamily: font,
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.3em",
          color: `${ink}99`,
          ...tnum,
        }}
      >
        // 01 — INVITATION
      </div>

      {/* Photo strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: photos[2] ? "2fr 1fr 1fr" : photos[1] ? "2fr 1fr" : "1fr",
          gap: 2,
          border: `1px solid ${ink}`,
        }}
      >
        <Photo src={photos[0] || photo} style={{ width: "100%", height: "100%", filter: "grayscale(1) contrast(1.05)" }} />
        {photos[1] && (
          <Photo src={photos[1]} style={{ width: "100%", height: "100%", filter: "grayscale(1) contrast(1.05)" }} />
        )}
        {photos[2] && (
          <Photo src={photos[2]} style={{ width: "100%", height: "100%", filter: "grayscale(1) contrast(1.05)" }} />
        )}
      </div>

      {/* Names + tagline */}
      <div>
        <div
          style={{
            fontFamily: font,
            fontSize: 8,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginBottom: "3%",
            ...tnum,
          }}
        >
          [ ANNUAL CORPORATE GALA ]
        </div>
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            lineHeight: 0.95,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            ...tnum,
          }}
        >
          {names}
        </div>
      </div>

      {/* Bottom keyline — venue, RSVP, tier stars */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `1px solid ${ink}`,
          paddingTop: "3%",
          fontFamily: font,
          fontSize: 8,
          fontWeight: 500,
          letterSpacing: "0.18em",
          ...tnum,
        }}
      >
        <span>{venue.toUpperCase()}</span>
        <span style={{ color: accent, letterSpacing: "0.4em" }}>★★★</span>
        <span style={{ color: accent }}>RSVP →</span>
      </div>
    </div>
  );
}
